<script lang="ts">
  import { fly } from "svelte/transition";
  import { modalStore, orderStore, orderDates } from "../../services/order-options-service";
  import { cart } from "../../services/cart-service";
  import type { OptionsObject, OrderObject } from "../../models/order.interface";
  import { fileStore } from "../../services/select-files";
  import { onDestroy } from "svelte";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let datasetMetadata: any;

  // Extract available formats and order options from metadata
  const formats = datasetMetadata?.ncei?.access?.order?.formats || [];
  const orderOptions = datasetMetadata?.ncei?.access?.order?.options || {};
  const maxFileCount = datasetMetadata?.ncei?.access?.order?.maxfilecount;

  // Set the initial format
  const defaultFormat = datasetMetadata?.previewoptions?.format?.[0] || formats[0];

  orderStore.setFormat(defaultFormat, orderOptions);

  // Helper function to determine option type
  function getOptionType(id: string): string {
    switch (id) {
      case "boundingBox":
        return "bbox";
      case "startDate":
      case "endDate":
        return "date";
      default:
        return "term";
    }
  }

  async function handleQuickAdd() {
    const orderOptions: OptionsObject[] = [];
    const optionTypeHandlers = {
      bbox: (id, option) => ["90,-180,-90,180"],
      date: (id, option) => {
        if ($fileStore.selectedFiles.length === 0) return [];
        if (id === "startdate") return [$orderDates.startDate];
        if (id === "enddate") return [$orderDates.endDate];
        return [];
      },
      term: (id, option) => {
        if (id === "stations" && $fileStore.stations && $fileStore.stations.size > 0) {
          return Array.from($fileStore.stations).map((station) => ({
            id: station.id,
            value: station.name
          }));
        }

        if (id === "datatypes" && $fileStore.datatypes && $fileStore.datatypes.size > 0) {
          return Array.from($fileStore.datatypes).map((dt) => ({
            id: dt.id,
            value: dt.name
          }));
        }

        if (id === "files" && $fileStore.selectedFiles && $fileStore.selectedFiles.length > 0) {
          return [...$fileStore.selectedFiles].map((file) => file.id);
        }

        // For all other term options, use defaults from option config
        return (
          option.params?.defaultValues ||
          (option.params?.values?.length ? [option.params.values[0]] : [])
        );
      },
      boolean: (id, option) => option.params?.defaultValues || ["No"]
    };

    Object.entries($orderStore.options).forEach(([id, option]) => {
      if (option.formats && !option.formats.includes($orderStore.selectedFormat)) {
        return;
      }

      // // Special handling for datatypes
      // if (id === "datatypes") {
      //   const dataTypeValues = handleDataTypes();
      //   if (dataTypeValues.length > 0) {
      //     orderOptions.push({
      //       id,
      //       type: "term",
      //       name: id,
      //       values: dataTypeValues
      //     });
      //   }
      //   return;
      // }

      // For all other options, use the type handlers
      const optionType = option.params?.type || getOptionType(id);
      const handler = optionTypeHandlers[optionType];
      if (handler) {
        const defaultValues = handler(id, option);
        if (defaultValues && defaultValues.length > 0) {
          orderOptions.push({
            id,
            type: optionType,
            name: id,
            values: defaultValues
          });
        }
      }
    });
    const sortedOptions = cart.sortOrderOptions(orderOptions);
    const orderObject: OrderObject = {
      orderId: null,
      email: null,
      datasetId: datasetMetadata.id,
      datasetName: datasetMetadata.name,
      format: $orderStore.selectedFormat,
      orderOptions: sortedOptions,
      files: $fileStore.selectedFiles.map((file) => ({
        archiveName: null,
        name: file.name,
        recId: file.id,
        tar: file.tar
      })),
      submitDate: null,
      orderStatus: null,
      orderType: datasetMetadata.ncei.access.order.type
    };
    cart.addItem(orderObject);
    clearSelections();
  }

  function handleFormat(e) {
    orderStore.setFormat(e.currentTarget.value, orderOptions);
  }

  function clearSelections() {
    fileStore.removeAll();
  }

  onDestroy(() => {
    orderStore.reset();
  });
</script>

<div id="cartAlert" class="cart-alert pt-3" transition:fly={{ y: 200, duration: 1000 }}>
  <div class="container-fluid">
    <div class="row">
      <div class="col d-flex justify-content-center">
        <span class="me-3"><b>Output Format:</b></span>
        {#each formats as f}
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              name="formatRadio"
              id="{f}RadioButton"
              value={f}
              on:change={handleFormat}
              checked={$orderStore.selectedFormat === f}
            />
            <label class="form-check-label" for="{f}RadioButton">
              {f}
            </label>
          </div>
        {/each}
      </div>
    </div>

    <div class="row mt-2 align-items-center">
      <div class="col d-flex justify-content-center align-items-center">
        <button
          type="button"
          class="btn ae-outline-secondary mx-2"
          data-bs-dismiss="offcanvas"
          id="clearSelections"
          aria-label="Clear Selections"
          on:click={clearSelections}
        >
          <i class="bi bi-x-circle"></i>
          Clear Selections
        </button>

        <!-- --- FIX #3: Use the robust variable in the template --- -->
        {#if $fileStore.selectedFiles.length <= maxFileCount}
          <button
            type="button"
            class="btn ae-secondary mx-2"
            id="configure"
            data-bs-toggle="modal"
            data-bs-target="#orderOptionsModal"
            on:click={modalStore.openModal}
          >
            <i class="bi bi-gear-fill"></i>
            Configure and Add
          </button>
          <button
            type="button"
            class="btn ae-secondary mx-2"
            id="quickAdd"
            on:click={handleQuickAdd}
          >
            + Quick Add
          </button>
        {:else}
          <button type="button" class="btn ae-secondary mx-2" id="configureDisabled" disabled>
            <i class="bi bi-gear-fill"></i>
            Configure and Add
          </button>
          <button type="button" class="btn ae-secondary mx-2" id="quickAddDisabled" disabled>
            + Quick Add
          </button>
        {/if}
        <TooltipHybridButton
          searchUrlQueryParam="quick-add"
          tooltip="Add to cart with default order options."
        />
        <!--        <span>-->
        <!--          <i class="bi bi-question-circle-fill ms-2" id="quickAddToolTip"></i>-->
        <!--          <Tooltip placement="right" target={`quickAddToolTip`}>-->
        <!--            Add to cart with default order options.-->
        <!--          </Tooltip>-->
        <!--        </span>-->
      </div>
    </div>

    <div class="row mt-2">
      <div class="col d-flex justify-content-center align-items-center">
        <b class="mx-2">Selected File Count/File Limit:</b>
        <!-- --- FIX #3: Use the robust variable here too --- -->
        <div class:text-danger={$fileStore.selectedFiles.length >= maxFileCount}>
          {$fileStore.selectedFiles.length}/{maxFileCount}
        </div>
        <TooltipHybridButton
          searchUrlQueryParam="order-limit"
          tooltip='Order sizes are restricted to reduce wait times. Each dataset has a file count limit. If
          these limits are exceeded the "Configure and Add" button will be disabled.'
        />
        <!--        <i class="bi bi-question-circle-fill ms-2" id="orderLimitToolTip"></i>-->
        <!--        <Tooltip placement="right" target={`orderLimitToolTip`}>-->
        <!--          Order sizes are restricted to reduce wait times. Each dataset has a file count limit. If-->
        <!--          these limits are exceeded the "Configure and Add" button will be disabled.-->
        <!--        </Tooltip>-->
      </div>
    </div>
  </div>
</div>

<style>
    .cart-alert {
        height: 20%;
        width: 100%;
        position: fixed;
        z-index: 3;
        bottom: 0;
        right: 0;
        background-color: #f1f3f6;
        overflow-y: hidden;
    }

    .form-check-input:focus {
        box-shadow: none;
    }
</style>
